<script setup lang="ts">
import { computed, ref } from "vue";

const { t } = useI18n();

const props = defineProps<{
  amount: string;
  suggestedAmounts: number[];
  formatAmount: (amount: number) => string;
  hasError?: boolean;
  errorMessage?: string | null;
  buttonStyle?: string | null;
  primaryColor?: string | null;
  /**
   * Boutons par ligne (3 par défaut, comme partout). La carte de la page
   * cagnotte en met 4 (20 / 50 / 100 / 200 €).
   */
  colonnes?: 3 | 4;
  /**
   * Champ « Montant libre » indépendant des boutons rapides : il reste vide
   * quand un bouton est choisi (au lieu de recopier son montant) et ne compte
   * que si l'on y tape un montant. Carte de la page cagnotte.
   */
  champLibre?: boolean;
}>();

// Classes écrites en entier : Tailwind ne voit pas les classes construites.
const grilleClass = computed(() => (props.colonnes === 4 ? "grid-cols-4" : "grid-cols-3"));

// Classe de bordure arrondie en fonction du style
const buttonRoundingClass = computed(() => {
  switch (props.buttonStyle) {
    case 'pill': return 'rounded-full';
    case 'square': return 'rounded-none';
    default: return 'rounded-lg';
  }
});

const emit = defineEmits<{
  "update:amount": [value: string];
}>();

// Vérifier si le montant actuel correspond à un montant suggéré
const isAmountSuggested = computed(() => {
  const numAmount = parseFloat(props.amount);
  return props.suggestedAmounts.includes(numAmount);
});

// Le donateur a-t-il tapé lui-même dans le champ (mode « champ libre ») ?
const saisieLibre = ref(false);
const valeurChamp = computed(() => {
  if (!props.champLibre) return props.amount;
  return saisieLibre.value || !isAmountSuggested.value ? props.amount : "";
});
const placeholderChamp = computed(() =>
  props.champLibre ? t("don.selecteurMontant.montantLibre") : t("don.selecteurMontant.autreMontant"),
);

// Sélectionner un montant suggéré
const selectSuggestedAmount = (amount: number) => {
  saisieLibre.value = false;
  emit("update:amount", String(amount));
};

// Gestion de l'input
const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  saisieLibre.value = true;
  emit("update:amount", value);
};
</script>

<template>
  <div>
    <!-- Montants suggérés -->
    <div class="grid gap-2 mb-3" :class="grilleClass">
      <button
        v-for="suggestedAmount in suggestedAmounts"
        :key="suggestedAmount"
        type="button"
        data-testid="amount-preset"
        :data-amount="suggestedAmount"
        :class="[
          'p-3 border transition-all duration-200 text-sm font-medium',
          buttonRoundingClass,
          parseFloat(amount) === suggestedAmount
            ? (primaryColor
                ? 'text-white shadow-md'
                : 'border-(--color-main) bg-(--color-main) text-white shadow-md')
            : 'border-gray-300 text-gray-900 hover:bg-gray-50',
        ]"
        :style="parseFloat(amount) === suggestedAmount && primaryColor ? {
          borderColor: primaryColor,
          background: primaryColor,
        } : undefined"
        @click="selectSuggestedAmount(suggestedAmount)"
      >
        {{ formatAmount(suggestedAmount) }}
      </button>
    </div>

    <!-- Input montant -->
    <div class="relative">
      <span
        class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
      >
        €
      </span>
      <input
        :value="valeurChamp"
        type="text"
        inputmode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        :placeholder="placeholderChamp"
        :class="[
          'w-full pl-8 pr-3 py-3 border rounded-lg transition-all duration-200 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-base outline-none focus:outline-none',
          hasError
            ? 'border-red-500 ring-1 ring-red-500'
            : !isAmountSuggested && amount
              ? (primaryColor ? 'ring-1' : 'border-blue-500 ring-1 ring-blue-500')
              : (primaryColor ? 'border-gray-300 dark:border-gray-700 focus:ring-1' : 'border-gray-300 dark:border-gray-700 focus:ring-1 focus:ring-blue-500 focus:border-blue-500')
        ]"
        :style="primaryColor && !hasError ? {
          '--tw-ring-color': !isAmountSuggested && amount ? primaryColor : undefined,
          borderColor: !isAmountSuggested && amount ? primaryColor : undefined,
        } : undefined"
        @input="handleInput"
      />
    </div>

    <!-- Message d'erreur -->
    <div
      v-if="hasError && errorMessage"
      class="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
    >
      <UIcon name="i-lucide-alert-circle" class="w-3.5 h-3.5" />
      {{ errorMessage }}
    </div>
  </div>
</template>
